import { create, StoreApi, UseBoundStore } from 'zustand'
import { createJSONStorage, persist, StateStorage } from 'zustand/middleware'
import { FilterIdentifier } from 'models/saved_view'
import { useLocation } from 'react-router-dom'
import { cacheFilters, shouldCacheFilters } from 'services/filterCache'
import { clientInstance } from 'utils/http-client'

export type FilterCriteria = 'one' | 'all' | 'neither'
export type ValueFilter = {
  value: string
  label: string
}
export type ValueFilterState = Record<string, ValueFilter[]>

/**
 * FIR-7845: key of the "Show Hidden" quick filter. Stored as a regular
 * value filter so it flows through `useFilterParams` (serialized by
 * query-string as `show_hidden=true` — the backend reads
 * `request.GET.get("show_hidden")`), renders as a removable FilterChip,
 * and participates in clear-all, saved views, and apply's page reset.
 */
export const SHOW_HIDDEN_FILTER_KEY = 'show_hidden'

/** The single value the Show Hidden quick filter stores when active. */
export const SHOW_HIDDEN_FILTER_VALUE: ValueFilter[] = [
  { value: 'true', label: 'On' },
]

/**
 * True when the (applied or pending) value filters carry the Show Hidden
 * flag — i.e. the table is in hidden-only mode (FIR-7845).
 */
export function isShowHiddenActive(valueFilters: ValueFilterState): boolean {
  return valueFilters[SHOW_HIDDEN_FILTER_KEY]?.[0]?.value === 'true'
}
export type SearchFilter = {
  value: string
  label: string
  elastic?: boolean
}
export type SearchFilterState = Record<string, SearchFilter>
export type RangeFilter = {
  min?: number
  max?: number
}
export type ZipCodeFilterState = [string[], number | null]

/**
 * Coerce a persisted/saved zip-code filter into the current
 * `[string[], number | null]` shape (FIR-8009).
 *
 * Saved filters and localStorage state written before the multi-zip migration
 * hold a bare string in slot [0] (the old single-zip shape). A string has
 * `.length`, so it slips past ZipcodeFilter's `isActive` guard and reaches
 * ZipChipInput, which crashes on `zips.map`. Normalizing at every untrusted
 * hydration boundary keeps the store's array invariant intact.
 */
export function normalizeZipCodeFilter(raw: unknown): ZipCodeFilterState {
  if (!Array.isArray(raw)) {
    return [[], null]
  }
  const [zips, radius] = raw
  let normalizedZips: string[]
  if (Array.isArray(zips)) {
    normalizedZips = zips.filter((z): z is string => typeof z === 'string')
  } else if (typeof zips === 'string' && zips.length > 0) {
    normalizedZips = [zips]
  } else {
    normalizedZips = []
  }
  const normalizedRadius = typeof radius === 'number' ? radius : null
  return [normalizedZips, normalizedRadius]
}

export type RangeFilterState = Record<string, RangeFilter>
export type FilterStore = UseBoundStore<StoreApi<FilterState>>

export const withHistoryDOMEvents = (store: any) => {
  const storageEventCallback = () => {
    store.persist.rehydrate()
  }

  window.addEventListener('popstate', storageEventCallback)

  return () => {
    window.removeEventListener('popstate', storageEventCallback)
  }
}

const getUrlSearch = () => {
  return window.location.search.slice(1)
}

/**
 * Helper to check if a filter value should be excluded from URL.
 */
function shouldExcludeFromUrl(key: string, value: any): boolean {
  const isTitles =
    key === 'filterTitles' ||
    key === 'filterPrefixes' ||
    key === 'filterDecimalScales'
  const isPending = key.includes('pending')
  const isZip =
    Array.isArray(value) && Array.isArray(value[0]) && value[0].length === 0
  const isEmpty =
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value ?? {}).length === 0
  const isPage = key === 'page'
  const isFilterId = key === 'filter_id'

  return isTitles || isPending || isZip || isEmpty || isPage || isFilterId
}

/**
 * Clean filter object for URL serialization by removing
 * empty values, pagination, filter_id, and metadata.
 */
function cleanFilterForUrl(filter: Record<string, any>): Record<string, any> {
  const cleaned: Record<string, any> = {}

  for (const [key, value] of Object.entries(filter)) {
    if (!shouldExcludeFromUrl(key, value)) {
      cleaned[key] = value
    }
  }

  return cleaned
}

/**
 * Update URL with new search params, handling empty state.
 */
function updateUrlSearchParams(searchParams: URLSearchParams): void {
  const urlData = searchParams.toString()
  if (urlData) {
    window.history.replaceState(null, '', `?${urlData}`)
  } else {
    window.history.replaceState(null, '', window.location.pathname)
  }
}

/**
 * Update URL to use filter_id instead of inline filter JSON.
 */
function updateUrlWithFilterId(key: string, filterId: string): void {
  const freshSearchParams = new URLSearchParams(getUrlSearch())
  freshSearchParams.delete(key)
  freshSearchParams.set(`${key}_filter_id`, filterId)
  updateUrlSearchParams(freshSearchParams)
}

/**
 * Update URL to use inline filter JSON instead of filter_id.
 */
function updateUrlWithInlineFilters(key: string, filterJson: string): void {
  const freshSearchParams = new URLSearchParams(getUrlSearch())
  freshSearchParams.set(key, filterJson)
  freshSearchParams.delete(`${key}_filter_id`)
  updateUrlSearchParams(freshSearchParams)
}

function createPersistentStorage(storeGetter: () => FilterStore): StateStorage {
  return {
    getItem: (key): string | null => {
      const searchParams = new URLSearchParams(getUrlSearch())

      // Check for filter_id first
      const filterIdKey = `${key}_filter_id`
      const filterId = searchParams.get(filterIdKey)

      if (filterId) {
        // We have a filter_id in URL - we'll need to fetch it asynchronously
        // For now, return from localStorage if available, otherwise null
        // The async fetch will be handled by a separate initialization function
        const localStorageValue = localStorage.getItem(key)
        if (localStorageValue) {
          const parsed = JSON.parse(localStorageValue)
          // Check if this localStorage has the same filter_id
          if (parsed?.filter_id === filterId) {
            return JSON.stringify({ state: parsed, version: 0 })
          }
        }
        // Return null for now - will be populated by async fetch
        return JSON.stringify({ state: { filter_id: filterId }, version: 0 })
      }

      // No filter_id - check for inline JSON in URL
      const urlValue = searchParams.get(key)
      const localStorageValue = localStorage.getItem(key)

      const jsonUrlValue = urlValue ? JSON.parse(urlValue) : null

      const jsonLocalStorageValue = JSON.parse(localStorageValue as string)

      if (getUrlSearch() && jsonUrlValue && jsonLocalStorageValue) {
        // Merge the URL and Local Storage
        for (const [k, v] of Object.entries(jsonLocalStorageValue)) {
          if (jsonUrlValue?.[k] === undefined) {
            jsonUrlValue[k] = v
          }
        }
      }

      // Put back zustand wrapper
      return JSON.stringify({ state: jsonUrlValue, version: 0 })
    },
    setItem: (key, newValue): void => {
      // Remove Zustand wrapper
      const filter = JSON.parse(newValue).state

      // Local Storage
      // Save entire state to local storage
      localStorage.setItem(key, JSON.stringify(filter))

      // Remove empty, pending, pagination, filter_id, and titles from the URL
      const urlFilter = cleanFilterForUrl(filter)

      const oldSearchParams = getUrlSearch()
      let searchParams = new URLSearchParams(getUrlSearch())

      const filterJson = JSON.stringify(urlFilter)
      const filterIdKey = `${key}_filter_id`

      // Check if we should use filter_id (for large filters > 512 bytes)
      // Transform to flat format before caching so backend can use directly
      if (shouldCacheFilters(urlFilter)) {
        // If we already have a filter_id and filters haven't changed, reuse it
        if (filter.filter_id) {
          updateUrlWithFilterId(key, filter.filter_id)
          return // Don't cache again
        }

        // Transform structured filter to flat API params format before caching
        // Use urlFilter (already cleaned of pagination) instead of filter
        const isCampaignPage = window.location.pathname.includes('/campaigns/')
        const flatFilters = buildFlatFiltersFromState(urlFilter, isCampaignPage)

        // cacheFilters() handles global deduplication internally
        const cachePromise = cacheFilters(flatFilters)

        // Cache filters (non-blocking) and update URL with filter_id
        cachePromise
          .then((filterId) => {
            // Update URL to use filter_id
            updateUrlWithFilterId(key, filterId)

            // Also store filter_id in the filter object for localStorage
            const filterWithId = { ...filter, filter_id: filterId }
            localStorage.setItem(key, JSON.stringify(filterWithId))

            // CRITICAL: Update Zustand store state with filter_id
            // This ensures subsequent page changes preserve the filter_id
            try {
              const store = storeGetter()
              store.setState({ filter_id: filterId })
            } catch (error) {
              console.error(
                '[FilterStore] Failed to update store state:',
                error
              )
            }
          })
          .catch((error) => {
            console.error('Failed to cache filters:', error)
            // Fall back to inline JSON if caching fails
            updateUrlWithInlineFilters(key, filterJson)
          })
        return // Return early, URL update happens in promise
      }

      // Small filters - use inline JSON
      searchParams.set(key, filterJson)
      searchParams.delete(filterIdKey)

      // If the search params are the same, don't update the URL
      if (searchParams.toString() === oldSearchParams) {
        searchParams = new URLSearchParams(oldSearchParams)
      }

      // If there's only "criteria" in the filter, should be empty
      if (Object.keys(urlFilter).length === 1 && urlFilter.criteria) {
        searchParams.delete(key)
        searchParams.delete(filterIdKey)
      }

      // Set the incoming filter to the object
      // if there are no filters, clear url search params
      updateUrlSearchParams(searchParams)
    },
    removeItem: (key): void => {
      const searchParams = new URLSearchParams(getUrlSearch())
      searchParams.delete(key)
      searchParams.delete(`${key}_filter_id`)
      window.location.search = searchParams.toString()
    },
  }
}

export interface FilterState {
  /*
            Pending filters are filters that have been selected but not yet applied.
            They should show up in the filter modal, but not yet be applied to the api call.
            */

  // Optional filter_id when filters are cached
  filter_id?: string

  registerFilterTitle: (key: string, title: string) => void
  filterTitles: Record<string, string>
  /** Currency / unit prefix per filter key (e.g. "$" for money fields).
   *  Registered by RangeFilter when its `prefix` prop is set and read by
   *  FilterChipRange to render "Lifetime Value: $15K – $80K" instead of
   *  the un-prefixed "15K – 80K". */
  registerFilterPrefix: (key: string, prefix: string) => void
  filterPrefixes: Record<string, string>
  /** Decimal precision per filter key — preserves the cents on
   *  non-round currency values (e.g. `$50,500.50` instead of the default
   *  toLocaleString output `$50,500.5`). Registered by RangeFilter when
   *  its `decimalScale` prop is > 0. */
  registerFilterDecimalScale: (key: string, decimalScale: number) => void
  filterDecimalScales: Record<string, number>

  // Zipcode
  zipCodeFilter: ZipCodeFilterState
  pendingZipCodeFilter: ZipCodeFilterState
  setPendingZipCodeFilter: (zips: string[], mileRadius: number | null) => void
  clearPendingZipCodeFilter: () => void
  clearZipCodeFilter: () => void

  // Value filters
  valueFilters: ValueFilterState
  clearValueFilter: (key: string) => void
  pendingValueFilters: ValueFilterState
  setPendingValueFilters: (key: string, values: ValueFilter[]) => void
  clearPendingValueFilter: (key: string) => void

  // Range filters
  rangeFilters: RangeFilterState
  pendingRangeFilters: RangeFilterState
  setPendingRangeFilters: (key: string, min?: number, max?: number) => void
  clearPendingRangeFilter: (key: string) => void
  clearRangeFilter: (key: string) => void

  // Criteria
  criteria: FilterCriteria
  pendingCriteria: FilterCriteria
  setPendingCriteria: (criteria: FilterCriteria) => void

  applyFilters: () => void
  clearAllFilters: () => void
  clearAllPendingFilters: () => void

  search: SearchFilterState
  pendingSearch: SearchFilterState
  setSearch: (
    key: string,
    value: string,
    label: string,
    elastic?: boolean
  ) => void
  clearSearch: (key: string) => void
  clearPendingSearch: (key: string) => void
  page: number
  setPage: (page: number) => void

  loadSavedFilter: (filters: Record<any, any>) => void
  syncPendingFilters: () => void
}

/**
 * The slice of FilterState persisted as a SavedView payload — what the manual
 * save-filter modal stores and `loadSavedFilter` restores.
 */
export interface SavedFilterPayload {
  valueFilters: ValueFilterState
  rangeFilters: RangeFilterState
  zipCodeFilter: ZipCodeFilterState
  search: SearchFilterState
  criteria: FilterCriteria
}

/**
 * Extract the SavedView payload slice from a store's (active, applied)
 * filter state.
 */
export function extractSavedFilterPayload(
  state: FilterState
): SavedFilterPayload {
  const { valueFilters, rangeFilters, zipCodeFilter, search, criteria } = state
  return { valueFilters, rangeFilters, zipCodeFilter, search, criteria }
}

/**
 * True when the payload carries at least one applied filter or search term.
 * `criteria` alone doesn't count: it only modifies how filters combine.
 * Values (not just keys) are checked, so a filter cleared back to an empty
 * array/range does not count as applied.
 */
export function savedFilterPayloadHasFilters(
  payload: SavedFilterPayload
): boolean {
  return (
    Object.values(payload.valueFilters).some((values) => values.length > 0) ||
    Object.values(payload.rangeFilters).some(
      (range) => range.min != null || range.max != null
    ) ||
    payload.zipCodeFilter[0].length > 0 ||
    Object.values(payload.search).some((term) => !!term.value)
  )
}

class FilterStoreRepo {
  private repo: Record<string, FilterStore> = {}

  getStore(
    storeName: FilterIdentifier,
    disablePersist: boolean = false
  ): FilterStore {
    if (!this.repo[storeName]) {
      this.repo[storeName] = filterStoreFactory(storeName, disablePersist)
    }

    return this.repo[storeName]
  }
}

export const filterStoreRepo = new FilterStoreRepo()

export function filterStoreFactory(
  storeName: FilterIdentifier,
  disablePersist = false
) {
  // Create a placeholder that will be populated after store creation
  let storeInstance: FilterStore | null = null
  const storeGetter = () => {
    if (!storeInstance) {
      throw new Error('Store not initialized yet')
    }
    return storeInstance
  }

  const store = create(
    persist<FilterState>(
      (set, get) => ({
        search: {},
        pendingSearch: {},
        setSearch(key, value, label, elastic = false) {
          set((state) => {
            if (value === '') {
              return {
                search: {},
                page: 1,
                filter_id: undefined,
              }
            }
            return {
              search: {
                ...state.search,
                [key]: {
                  value: value,
                  label: label,
                  elastic: elastic,
                } as SearchFilter,
              },
              page: 1,
              filter_id: undefined,
            }
          })
        },
        clearSearch: (key: string) => {
          set((state) => {
            const search = { ...state.search }
            delete search[key]
            return { search: search, page: 1, filter_id: undefined }
          })
        },
        clearPendingSearch: (key: string) => {
          set((state) => {
            const search = { ...state.search }
            delete search[key]
            return { pendingSearch: search }
          })
        },

        filterTitles: {},
        filterPrefixes: {},
        filterDecimalScales: {},

        zipCodeFilter: [[], null],
        pendingZipCodeFilter: [[], null],

        valueFilters: {},
        pendingValueFilters: {},

        rangeFilters: {},
        pendingRangeFilters: {},

        criteria: 'all',
        pendingCriteria: 'all',

        page: 1,

        setPage: (page: number) => {
          set((state) => {
            // Preserve filter_id when changing pages
            return { page: page, filter_id: state.filter_id }
          })
        },

        setPendingValueFilters: (key: string, values: ValueFilter[]) => {
          set((state) => {
            const pending = {
              ...state.pendingValueFilters,
              [key]: values,
            }
            if (!values.length) {
              delete pending[key]
            }
            return { pendingValueFilters: pending }
          })
        },
        clearPendingValueFilter: (key) => {
          set((state) => {
            const pending = { ...state.pendingValueFilters }
            delete pending[key]
            return { pendingValueFilters: pending }
          })
        },

        setPendingRangeFilters(key, min, max) {
          set((state) => {
            const pending = {
              ...state.pendingRangeFilters,
              [key]: { min, max },
            }
            return { pendingRangeFilters: pending }
          })
        },
        clearPendingRangeFilter(key) {
          set((state) => {
            const pending = { ...state.pendingRangeFilters }
            delete pending[key]
            return { pendingRangeFilters: pending }
          })
        },

        setPendingZipCodeFilter: (
          zips: string[],
          mileRadius: number | null
        ) => {
          set(() => {
            return { pendingZipCodeFilter: [zips, mileRadius] }
          })
        },
        clearPendingZipCodeFilter: () => {
          set(() => {
            return { pendingZipCodeFilter: [[], null] }
          })
        },

        setPendingCriteria: (criteria: FilterCriteria) => {
          set(() => {
            return { pendingCriteria: criteria }
          })
        },

        clearAllPendingFilters: () => {
          set(() => {
            return {
              pendingValueFilters: {},
              pendingRangeFilters: {},
              pendingZipCodeFilter: [[], null],
              pendingCriteria: 'all',
            }
          })
        },

        clearValueFilter: (key) => {
          set((state) => {
            const values = { ...state.valueFilters }
            delete values[key]
            return { valueFilters: values, filter_id: undefined }
          })
        },
        clearZipCodeFilter: () => {
          set(() => {
            return { zipCodeFilter: [[], null], filter_id: undefined }
          })
        },
        clearRangeFilter(key) {
          set((state) => {
            const ranges = { ...state.rangeFilters }
            delete ranges[key]
            return { rangeFilters: ranges, filter_id: undefined }
          })
        },

        applyFilters: async () => {
          const state = get()

          // Build flat filters from pending state
          const isCampaignPage =
            window.location.pathname.includes('/campaigns/')
          const flatFilters = buildFlatFiltersFromState(
            {
              valueFilters: state.pendingValueFilters,
              rangeFilters: state.pendingRangeFilters,
              zipCodeFilter: state.pendingZipCodeFilter,
              search: state.pendingSearch,
              criteria: state.pendingCriteria,
            },
            isCampaignPage
          )

          // Get filter_id (caches if needed)
          const filterId = await getFilterIdForFilters(flatFilters)

          // Now update store with filter_id already ready
          set(() => {
            return {
              valueFilters: state.pendingValueFilters,
              rangeFilters: state.pendingRangeFilters,
              zipCodeFilter: state.pendingZipCodeFilter,
              criteria: state.pendingCriteria,
              search: state.pendingSearch,
              // Reset page to 1 when applying filters
              page: 1,
              // Set filter_id from cache
              filter_id: filterId,
            }
          })
        },
        clearAllFilters: () => {
          set(() => {
            return {
              valueFilters: {},
              pendingValueFilters: {},
              rangeFilters: {},
              pendingRangeFilters: {},
              zipCodeFilter: [[], null],
              pendingZipCodeFilter: [[], null],
              criteria: 'all',
              pendingCriteria: 'all',
              search: {},
              page: 1,
              filter_id: undefined,
            }
          })
        },

        registerFilterTitle: (key: string, title: string) => {
          set((state) => {
            return { filterTitles: { ...state.filterTitles, [key]: title } }
          })
        },

        registerFilterPrefix: (key: string, prefix: string) => {
          set((state) => {
            return {
              filterPrefixes: { ...state.filterPrefixes, [key]: prefix },
            }
          })
        },

        registerFilterDecimalScale: (key: string, decimalScale: number) => {
          set((state) => {
            return {
              filterDecimalScales: {
                ...state.filterDecimalScales,
                [key]: decimalScale,
              },
            }
          })
        },

        loadSavedFilter: (filters: Record<any, any>) => {
          set(() => {
            return {
              pendingValueFilters: filters.valueFilters,
              pendingRangeFilters: filters.rangeFilters,
              // Normalize the saved payload: filters saved before the
              // multi-zip migration carry a bare string here (FIR-8009).
              pendingZipCodeFilter: normalizeZipCodeFilter(
                filters.zipCodeFilter
              ),
              pendingCriteria: filters.criteria,
              pendingSearch: filters.search,
            }
          })
        },

        syncPendingFilters: () => {
          set((state) => {
            return {
              pendingValueFilters: state.valueFilters,
              pendingRangeFilters: state.rangeFilters,
              pendingZipCodeFilter: state.zipCodeFilter,
              pendingCriteria: state.criteria,
              pendingSearch: state.search,
            }
          })
        },
      }),
      {
        name: storeName,
        storage: disablePersist
          ? undefined
          : (createJSONStorage<FilterStore>(() =>
              createPersistentStorage(storeGetter)
            ) as any),
        // localStorage written before the multi-zip migration can hold a bare
        // string in zipCodeFilter[0]; normalize on rehydration so ZipChipInput
        // never sees a non-array (FIR-8009). Mirrors zustand's default shallow
        // merge, plus coercion of the two zip slices.
        merge: (persistedState, currentState) => {
          const persisted = (persistedState ?? {}) as Partial<FilterState>
          return {
            ...currentState,
            ...persisted,
            zipCodeFilter: normalizeZipCodeFilter(persisted.zipCodeFilter),
            pendingZipCodeFilter: normalizeZipCodeFilter(
              persisted.pendingZipCodeFilter
            ),
          }
        },
      }
    )
  )

  // Populate storeInstance now that store is created
  storeInstance = store

  withHistoryDOMEvents(store)

  // Check if URL has filter_id and fetch if needed
  const searchParams = new URLSearchParams(window.location.search)
  const filterIdKey = `${storeName}_filter_id`
  const filterId = searchParams.get(filterIdKey)

  if (filterId) {
    // Fetch filters asynchronously using authenticated client
    clientInstance
      .get('common/filters/cache/', {
        params: { filter_id: filterId },
      })
      .then((response) => {
        const flatFilters = response.data.filters
        // Convert flat params back to structured FilterStore format
        const structuredFilters = parseFlatFiltersToStore(flatFilters)

        // Update the store with fetched filters
        // Also populate pending state so filter modal shows current selections
        store.setState({
          ...structuredFilters,
          filter_id: filterId,
          // Copy applied filters to pending state
          pendingValueFilters: structuredFilters.valueFilters || {},
          pendingRangeFilters: structuredFilters.rangeFilters || {},
          pendingZipCodeFilter: normalizeZipCodeFilter(
            structuredFilters.zipCodeFilter
          ),
          pendingSearch: structuredFilters.search || {},
          pendingCriteria: structuredFilters.criteria || 'all',
        })
        // Also update localStorage
        localStorage.setItem(
          storeName,
          JSON.stringify({ ...structuredFilters, filter_id: filterId })
        )
      })
      .catch((error) => {
        console.error('Failed to fetch cached filters:', error)
      })
  }

  return store
}

/**
 * Build value filter params (simple key-value arrays).
 */
function buildValueFilterParams(
  valueFilters: ValueFilterState
): Record<string, string[]> {
  const params: Record<string, string[]> = {}

  for (const [key, values] of Object.entries(valueFilters)) {
    params[key] = values.map((v) => v.value)
  }

  return params
}

/**
 * Build range filter params (min/max boundaries).
 */
function buildRangeFilterParams(
  rangeFilters: RangeFilterState
): Record<string, string> {
  const params: Record<string, string> = {}

  for (const [key, range] of Object.entries(rangeFilters)) {
    if (range.min !== undefined) {
      params[`${key}_greater`] = range.min.toString()
    }
    if (range.max !== undefined) {
      params[`${key}_less`] = range.max.toString()
    }
  }

  return params
}

/**
 * Build zip code filter params.
 */
function buildZipCodeFilterParams(
  zipCodeFilter: ZipCodeFilterState
): Record<string, string[] | number> {
  const [zips, radius] = zipCodeFilter
  if (!zips || zips.length === 0) {
    return {}
  }
  return {
    zipcode: zips,
    ...(radius != null ? { radius } : {}),
  }
}

/**
 * Build search filter params, handling elastic search separately.
 */
function buildSearchFilterParams(
  searchFilters: SearchFilterState
): Record<string, string> {
  const params: Record<string, string> = {}
  const elasticQueryParts: string[] = []

  for (const [key, value] of Object.entries(searchFilters)) {
    if (value.value) {
      if (value.elastic) {
        elasticQueryParts.push(`${key}:${value.value}`)
      } else {
        params[key] = value.value
      }
    }
  }

  const elasticQuery = elasticQueryParts.join(';')
  if (elasticQuery) {
    params.search_query = elasticQuery
  }

  return params
}

/**
 * Build flat filter params from a filter state object.
 * Helper to avoid duplicating buildFilterParams() calls with defaults.
 */
function buildFlatFiltersFromState(
  filter: Partial<FilterState>,
  isCampaignPage?: boolean
): Record<string, any> {
  return buildFilterParams(
    filter.valueFilters || {},
    filter.rangeFilters || {},
    filter.zipCodeFilter ?? [[], null],
    filter.search || {},
    filter.criteria || 'all',
    isCampaignPage
  )
}

/**
 * Parse flat filter params back into structured FilterStore format.
 * Reverse of buildFilterParams() - used when hydrating from cache.
 */
export function parseFlatFiltersToStore(
  flatFilters: Record<string, any>
): Partial<FilterState> {
  const valueFilters: ValueFilterState = {}
  const rangeFilters: RangeFilterState = {}
  const search: SearchFilterState = {}
  const zipCodeFilter: ZipCodeFilterState = [[], null]
  let criteria: FilterCriteria = 'all'

  for (const [key, value] of Object.entries(flatFilters)) {
    // Skip pagination and meta params
    if (['page', 'page_size', 'limit', 'campaign_page'].includes(key)) {
      continue
    }

    // Handle criteria/type
    if (key === 'type') {
      criteria = value as FilterCriteria
      continue
    }

    // Handle range filters (field_greater, field_less)
    if (key.endsWith('_greater')) {
      const fieldName = key.replace('_greater', '')
      if (!rangeFilters[fieldName]) {
        rangeFilters[fieldName] = {}
      }
      // Keep date strings (YYYY-MM-DD) as strings, convert valid numbers for numeric ranges
      // Trim to handle whitespace, then check if it's a valid number
      const trimmed = String(value).trim()
      const numValue = Number(trimmed)
      rangeFilters[fieldName].min =
        trimmed && !isNaN(numValue) ? numValue : value
      continue
    }
    if (key.endsWith('_less')) {
      const fieldName = key.replace('_less', '')
      if (!rangeFilters[fieldName]) {
        rangeFilters[fieldName] = {}
      }
      // Keep date strings (YYYY-MM-DD) as strings, convert valid numbers for numeric ranges
      // Trim to handle whitespace, then check if it's a valid number
      const trimmed = String(value).trim()
      const numValue = Number(trimmed)
      rangeFilters[fieldName].max =
        trimmed && !isNaN(numValue) ? numValue : value
      continue
    }

    // Handle zipcode (string from legacy URLs, or array from repeated keys)
    if (key === 'zipcode') {
      zipCodeFilter[0] = Array.isArray(value)
        ? (value as string[])
        : [value as string]
      continue
    }
    if (key === 'radius') {
      zipCodeFilter[1] = Number(value)
      continue
    }

    // Handle elastic search query
    if (key === 'search_query') {
      // Parse elastic query format: "field1:value1;field2:value2"
      const parts = (value as string).split(';')
      for (const part of parts) {
        const [field, searchValue] = part.split(':')
        if (field && searchValue) {
          search[field] = {
            value: searchValue,
            label: searchValue,
            elastic: true,
          }
        }
      }
      continue
    }

    // Handle array values (value filters)
    if (Array.isArray(value)) {
      valueFilters[key] = value.map((v) => ({
        value: String(v),
        label: String(v),
      }))
      continue
    }

    // Handle single string values (search filters)
    if (typeof value === 'string' && value) {
      search[key] = {
        value,
        label: value,
        elastic: false,
      }
      continue
    }
  }

  return {
    valueFilters,
    rangeFilters,
    search,
    zipCodeFilter,
    criteria,
  }
}

/**
 * Get filter_id for filters, caching them if needed.
 * Returns filter_id for large filters (>512 bytes), undefined for small filters.
 */
async function getFilterIdForFilters(
  flatFilters: Record<string, any>
): Promise<string | undefined> {
  // Check if we should cache (large filters > 512 bytes)
  if (!shouldCacheFilters(flatFilters)) {
    return undefined
  }

  try {
    // Cache and return filter_id
    return await cacheFilters(flatFilters)
  } catch (error) {
    console.error('[FilterStore] Failed to cache filters:', error)
    // Continue without filter_id if caching fails
    return undefined
  }
}

/**
 * Build filter params from FilterStore state.
 * Combines all filter types into flat API query params.
 */
export function buildFilterParams(
  valueFilters: FilterState['valueFilters'],
  rangeFilters: FilterState['rangeFilters'],
  zipCodeFilter: FilterState['zipCodeFilter'],
  searchFilters: FilterState['search'],
  criteria: FilterState['criteria'],
  isCampaignPage?: boolean,
  pageSize?: number,
  page?: number
): Record<string, any> {
  // Always build flat params - the http-client interceptor will cache them if needed
  // FilterStore's filter_id is ONLY for URL sharing, not for API queries

  const params: Record<string, any> = {
    type: criteria,
    ...buildValueFilterParams(valueFilters),
    ...buildRangeFilterParams(rangeFilters),
    ...buildZipCodeFilterParams(zipCodeFilter),
    ...buildSearchFilterParams(searchFilters),
  }

  if (isCampaignPage) {
    params.campaign_page = true
  }

  if (pageSize) {
    params.page_size = pageSize
  }

  if (page) {
    params.page = page
  }

  return params
}

export function useFilterParams(
  store: FilterStore,
  pageSize?: number,
  page?: number
) {
  const filters = store()
  const {
    criteria,
    valueFilters,
    rangeFilters,
    zipCodeFilter,
    search,
    filter_id,
  } = filters
  const location = useLocation()
  const isCampaignPage = location.pathname.includes('/campaigns/')

  // If filter_id exists, use it instead of building full params
  if (filter_id) {
    const params: Record<string, any> = { filter_id }

    if (pageSize) {
      params.page_size = pageSize
    }
    if (page) {
      params.page = page
    }

    return params
  }

  return buildFilterParams(
    valueFilters,
    rangeFilters,
    zipCodeFilter,
    search,
    criteria,
    isCampaignPage,
    pageSize,
    page
  )
}
